/**
 * Cookie Consent Helper
 * Manages cookie consent preferences in localStorage
 */

// Cookie consent storage key
const COOKIE_CONSENT_KEY = "ts_cookie_consent";
const COOKIE_CONSENT_TIMESTAMP_KEY = "ts_cookie_consent_timestamp";

// Cookie categories
export const COOKIE_CATEGORIES = {
  ESSENTIAL: "essential",
  ANALYTICS: "analytics",
  MARKETING: "marketing",
  PREFERENCES: "preferences",
};

/**
 * Check if user has made a consent choice
 * @returns {boolean} True if user has made a choice
 */
export const hasConsentChoice = () => {
  return localStorage.getItem(COOKIE_CONSENT_KEY) !== null;
};

/**
 * Get current cookie consent preferences
 * @returns {Object|null} Consent preferences or null if not set
 */
export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    return consent ? JSON.parse(consent) : null;
  } catch (error) {
    console.error("Error reading cookie consent:", error);
    return null;
  }
};

/**
 * Save cookie consent preferences
 * @param {Object} consent - Consent preferences object
 */
export const saveCookieConsent = (consent) => {
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    localStorage.setItem(COOKIE_CONSENT_TIMESTAMP_KEY, new Date().toISOString());
  } catch (error) {
    console.error("Error saving cookie consent:", error);
  }
};

/**
 * Accept all cookies
 * @returns {Object} Consent object with all categories enabled
 */
export const acceptAllCookies = () => {
  const consent = {
    [COOKIE_CATEGORIES.ESSENTIAL]: true,
    [COOKIE_CATEGORIES.ANALYTICS]: true,
    [COOKIE_CATEGORIES.MARKETING]: true,
    [COOKIE_CATEGORIES.PREFERENCES]: true,
  };
  saveCookieConsent(consent);
  return consent;
};

/**
 * Accept only essential cookies
 * @returns {Object} Consent object with only essential cookies enabled
 */
export const acceptEssentialOnly = () => {
  const consent = {
    [COOKIE_CATEGORIES.ESSENTIAL]: true,
    [COOKIE_CATEGORIES.ANALYTICS]: false,
    [COOKIE_CATEGORIES.MARKETING]: false,
    [COOKIE_CATEGORIES.PREFERENCES]: false,
  };
  saveCookieConsent(consent);
  return consent;
};

/**
 * Get timestamp of when consent was last updated
 * @returns {string|null} ISO timestamp string or null
 */
export const getConsentTimestamp = () => {
  return localStorage.getItem(COOKIE_CONSENT_TIMESTAMP_KEY);
};

/**
 * Clear cookie consent (for testing/debugging)
 */
export const clearCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  localStorage.removeItem(COOKIE_CONSENT_TIMESTAMP_KEY);
};

/**
 * Check if a specific category is consented
 * @param {string} category - Cookie category to check
 * @returns {boolean} True if category is consented
 */
export const isCategoryConsented = (category) => {
  const consent = getCookieConsent();
  return consent ? consent[category] === true : false;
};

/**
 * Check if analytics cookies are allowed
 * @returns {boolean}
 */
export const canUseAnalytics = () => {
  return isCategoryConsented(COOKIE_CATEGORIES.ANALYTICS);
};

/**
 * Check if marketing cookies are allowed
 * @returns {boolean}
 */
export const canUseMarketing = () => {
  return isCategoryConsented(COOKIE_CATEGORIES.MARKETING);
};

/**
 * Check if preference cookies are allowed
 * @returns {boolean}
 */
export const canUsePreferences = () => {
  return isCategoryConsented(COOKIE_CATEGORIES.PREFERENCES);
};
